/**
 * ProjectileSystem — framework-free pure core + thin Phaser glue.
 *
 * Pure core (no Phaser import):
 *   - A projectile has position, direction (±1), speed, range-travelled, alive.
 *   - tick() advances it one frame and marks it dead when maxRange is exceeded.
 *   - collides() is a simple centre-to-hitbox overlap helper for first-hit detection.
 *
 * Phaser glue (ProjectileManager):
 *   - Spawns a small rectangle sprite each frame when a projectile is created.
 *   - Advances each projectile every frame via tick().
 *   - Checks overlap with registered Combatant hurtboxes via the existing
 *     CombatSystem.spawn() path — spawns a single-frame Hitbox on first contact.
 *   - Despawns on hit, range exceeded, or offscreen.
 *
 * Pattern mirrors TauntSystem (pure state) + Player (Phaser adapter).
 * Tunables live on ProjectileDef; callers set speed, range, size, damage per def.
 */

import type { HitSpec, Combatant, CombatSystem } from '@/systems/Combat';
import { Hitbox } from '@/systems/Combat';
import { boxFromCenter } from '@/systems/box';

// ── Pure core ──────────────────────────────────────────────────────────────────

/** Per-type tuning for a class of projectile (e.g. Boundary Smash shockwave). */
export interface ProjectileDef {
  /** Horizontal speed in pixels per frame. */
  speed: number;
  /** Maximum horizontal distance the projectile travels before despawning. */
  maxRange: number;
  /** Hitbox width (px). */
  w: number;
  /** Hitbox height (px). */
  h: number;
  /** Hit parameters applied on first contact. */
  spec: HitSpec;
  /**
   * When true the projectile reverses direction once it reaches half its
   * maxRange (boomerang arc), continuing until the cumulative rangeTravelled
   * reaches maxRange.  Useful for Kris's Hammer Throw (MY-2418).
   */
  boomerang?: boolean;
}

/** Runtime state of a single live projectile. */
export interface ProjectileState {
  /** Current world X position. */
  x: number;
  /** World Y position (fixed at spawn, no arc). */
  y: number;
  /** Travel direction: +1 = right, -1 = left. */
  dir: 1 | -1;
  /** Cumulative distance travelled so far (px). */
  rangeTravelled: number;
  /** False once the projectile has expired (hit, max range, or offscreen). */
  alive: boolean;
}

/**
 * Create a new projectile state.
 *
 * @param x   Spawn world X.
 * @param y   Spawn world Y.
 * @param dir Travel direction (+1 = right, -1 = left).
 */
export function createProjectile(x: number, y: number, dir: 1 | -1): ProjectileState {
  return { x, y, dir, rangeTravelled: 0, alive: true };
}

/**
 * Advance a projectile one frame according to its def.
 * Marks it dead when maxRange is exceeded.
 *
 * Boomerang: when def.boomerang is true the projectile reverses direction
 * once rangeTravelled reaches half of maxRange, then continues until the
 * cumulative distance reaches maxRange and it expires normally.
 *
 * @returns The mutated (or newly dead) state.
 */
export function tickProjectile(state: ProjectileState, def: ProjectileDef): ProjectileState {
  if (!state.alive) return state;
  // Boomerang flip: reverse at the halfway point.
  if (def.boomerang && state.rangeTravelled < def.maxRange / 2) {
    const nextRange = state.rangeTravelled + def.speed;
    if (nextRange >= def.maxRange / 2) {
      // Reverse direction for the return arc.
      state.dir = (state.dir * -1) as 1 | -1;
    }
  }
  const dx = state.dir * def.speed;
  state.x += dx;
  state.rangeTravelled += def.speed;
  if (state.rangeTravelled >= def.maxRange) {
    state.alive = false;
  }
  return state;
}

/**
 * Returns true when the projectile's centre overlaps the given axis-aligned
 * bounding box (target hurtbox). Used for first-hit detection.
 *
 * @param projX  Projectile centre X.
 * @param projY  Projectile centre Y.
 * @param def    Projectile def (provides w, h for the projectile hitbox).
 * @param target Hurtbox of the potential target.
 */
export function projectileCollides(
  projX: number,
  projY: number,
  def: ProjectileDef,
  target: { x: number; y: number; w: number; h: number },
): boolean {
  const left = projX - def.w / 2;
  const right = projX + def.w / 2;
  const top = projY - def.h / 2;
  const bottom = projY + def.h / 2;
  const tRight = target.x + target.w;
  const tBottom = target.y + target.h;
  return right > target.x && left < tRight && bottom > target.y && top < tBottom;
}

// ── Phaser glue ────────────────────────────────────────────────────────────────

import Phaser from 'phaser';

/** A live projectile managed by the ProjectileManager. */
interface ManagedProjectile {
  state: ProjectileState;
  def: ProjectileDef;
  /** The visual rectangle sprite in the Phaser scene. */
  visual: Phaser.GameObjects.Rectangle;
  /** The Combatant that fired the projectile (excluded from self-hits). */
  owner: Combatant;
  /** Set of combatant IDs already hit (prevents multi-hit). */
  alreadyHit: Set<number>;
}

/**
 * ProjectileManager — Phaser-aware wrapper that owns the lifecycle of all live
 * projectiles in a scene frame.
 *
 * Usage (from Player or StageScene):
 *   const projectiles = new ProjectileManager(scene, combat, combatants);
 *   // each frame:
 *   projectiles.update(activeCombatants);
 *   // to spawn:
 *   projectiles.spawn(owner, x, y, dir, def);
 */
export class ProjectileManager {
  private readonly scene: Phaser.Scene;

  private readonly combat: CombatSystem;

  private active: ManagedProjectile[] = [];

  constructor(scene: Phaser.Scene, combat: CombatSystem) {
    this.scene = scene;
    this.combat = combat;
  }

  /**
   * Spawn a new projectile.
   *
   * @param owner   The Combatant who fired it (exempted from self-hit).
   * @param x       World X of the projectile centre at spawn.
   * @param y       World Y of the projectile centre at spawn.
   * @param dir     Travel direction (+1 right, -1 left).
   * @param def     Tuning params (speed, range, hitbox size, damage spec).
   */
  spawn(owner: Combatant, x: number, y: number, dir: 1 | -1, def: ProjectileDef): void {
    const state = createProjectile(x, y, dir);
    const visual = this.scene.add
      .rectangle(x, y, def.w, def.h, 0xfff176)
      .setDepth(10)
      .setAlpha(0.85);
    this.active.push({ state, def, visual, owner, alreadyHit: new Set() });
  }

  /**
   * Advance all live projectiles one frame.
   * Checks collision against every combatant in the provided list,
   * delivers damage via the existing Combat Hitbox path, and despawns
   * projectiles that have expired, hit, or gone offscreen.
   *
   * @param combatants All current Combatants (registered in the CombatSystem).
   * @param worldWidth  Scene world width for offscreen culling.
   */
  update(combatants: readonly Combatant[], worldWidth: number): void {
    const toRemove: ManagedProjectile[] = [];

    for (const mp of this.active) {
      tickProjectile(mp.state, mp.def);

      // Despawn on range exhaustion or offscreen.
      if (!mp.state.alive || mp.state.x < 0 || mp.state.x > worldWidth) {
        toRemove.push(mp);
        continue;
      }

      // Move visual.
      mp.visual.setPosition(mp.state.x, mp.state.y);

      // Collision check — first unvisited opponent.
      let hit = false;
      for (const c of combatants) {
        if (c === mp.owner) continue;
        if (c.team === mp.owner.team) continue;
        if (c.isKO) continue;
        if (mp.alreadyHit.has(c.combatId)) continue;

        const hb = c.worldHurtbox();
        if (projectileCollides(mp.state.x, mp.state.y, mp.def, hb)) {
          mp.alreadyHit.add(c.combatId);
          // Deliver damage via the existing Combat path: spawn a 1-frame Hitbox
          // at the projectile's current position.
          const box = boxFromCenter(mp.state.x, mp.state.y, mp.def.w, mp.def.h);
          const oneFrameHitbox = new Hitbox(mp.owner, box, mp.def.spec, 1);
          this.combat.spawn(oneFrameHitbox);
          mp.state.alive = false;
          hit = true;
          break; // one hit per frame per projectile
        }
      }

      if (hit) {
        toRemove.push(mp);
      }
    }

    // Cull expired projectiles and destroy their visuals.
    for (const mp of toRemove) {
      mp.visual.destroy();
    }
    this.active = this.active.filter((mp) => !toRemove.includes(mp));
  }

  /** Destroy all active projectiles (scene shutdown). */
  clear(): void {
    for (const mp of this.active) {
      mp.visual.destroy();
    }
    this.active = [];
  }

  /** Read-only access to active projectile states (for tests / debug). */
  get activeProjectiles(): readonly ProjectileState[] {
    return this.active.map((mp) => mp.state);
  }

  /** Count of currently live projectiles. */
  get count(): number {
    return this.active.length;
  }
}
